import { useState, useEffect } from 'react';
import { Calendar, DollarSign, Flame, PiggyBank, Plus, Trash2, Info } from 'lucide-react';

// Exchange rates (mock - in production, fetch from API)
const EXCHANGE_RATES: Record<string, { rate: number; symbol: string }> = {
  'USD': { rate: 1, symbol: '$' },
  'EUR': { rate: 0.92, symbol: '€' },
  'GBP': { rate: 0.79, symbol: '£' },
  'JPY': { rate: 149.50, symbol: '¥' },
  'CHF': { rate: 0.88, symbol: 'CHF' },
  'CAD': { rate: 1.36, symbol: 'C$' },
  'AUD': { rate: 1.53, symbol: 'A$' },
};

interface PensionIncome {
  id: string;
  name: string;
  age: number;
  monthlyAmount: number;
}

interface FIRECalculatorProps {
  currentPortfolioValue: number;
  currency: string;
}

export default function FIRECalculator({ currentPortfolioValue, currency: defaultCurrency }: FIRECalculatorProps) {
  // Initialize state with localStorage values or defaults
  const [currentAge, setCurrentAge] = useState<number>(() => {
    const saved = localStorage.getItem('fireCalculator_currentAge');
    return saved ? Number(saved) : 30;
  });
  const [retirementAge, setRetirementAge] = useState<number>(55);
  const [monthlyContribution, setMonthlyContribution] = useState<number>(() => {
    const saved = localStorage.getItem('fireCalculator_monthlyContribution');
    return saved ? Number(saved) : 1000;
  });
  const [annualReturn, setAnnualReturn] = useState<number>(7);
  const [inflationRate, setInflationRate] = useState<number>(2.5);
  const [withdrawalRate, setWithdrawalRate] = useState<number>(4);
  const [targetMonthlyIncome, setTargetMonthlyIncome] = useState<number>(5000);
  const [pensions, setPensions] = useState<PensionIncome[]>(() => {
    const saved = localStorage.getItem('fireCalculator_pensions');
    return saved ? JSON.parse(saved) : [];
  });
  const [mode, setMode] = useState<'age' | 'income'>('age');
  const [showInfo, setShowInfo] = useState(false);
  const [currency, setCurrency] = useState<string>(defaultCurrency);

  // Save current age to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fireCalculator_currentAge', currentAge.toString());
  }, [currentAge]);

  // Save monthly contribution to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fireCalculator_monthlyContribution', monthlyContribution.toString());
  }, [monthlyContribution]);

  // Save pensions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('fireCalculator_pensions', JSON.stringify(pensions));
  }, [pensions]);

  // Calculate future portfolio value
  const calculateFutureValue = (years: number): number => {
    const months = years * 12;
    const monthlyRate = annualReturn / 100 / 12;
    
    // Future value of current portfolio
    const fvCurrent = currentPortfolioValue * Math.pow(1 + monthlyRate, months);
    
    // Future value of monthly contributions
    const fvContributions = monthlyContribution * 
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
    
    return fvCurrent + fvContributions;
  };

  // Calculate inflation-adjusted value
  const adjustForInflation = (amount: number, years: number): number => {
    return amount / Math.pow(1 + inflationRate / 100, years);
  };

  // Calculate monthly income from portfolio
  const calculateMonthlyIncome = (portfolioValue: number): number => {
    return (portfolioValue * (withdrawalRate / 100)) / 12;
  };

  // Calculate required portfolio for target income
  const calculateRequiredPortfolio = (monthlyIncome: number): number => {
    return (monthlyIncome * 12) / (withdrawalRate / 100);
  };

  // Calculate years to reach target
  const calculateYearsToTarget = (targetPortfolio: number): number => {
    if (monthlyContribution === 0 && currentPortfolioValue >= targetPortfolio) {
      return 0;
    }
    
    const monthlyRate = annualReturn / 100 / 12;
    let months = 0;
    let value = currentPortfolioValue;
    
    while (value < targetPortfolio && months < 1200) { // Max 100 years
      value = value * (1 + monthlyRate) + monthlyContribution;
      months++;
    }
    
    return months / 12;
  };

  // Get pension income at specific age
  const getPensionIncomeAtAge = (age: number): number => {
    return pensions
      .filter(p => age >= p.age)
      .reduce((sum, p) => sum + p.monthlyAmount, 0);
  };

  // Main calculations
  const yearsToRetirement = retirementAge - currentAge;
  const futurePortfolioValue = calculateFutureValue(yearsToRetirement);
  const monthlyIncomeAtRetirement = calculateMonthlyIncome(futurePortfolioValue);
  const inflationAdjustedIncome = adjustForInflation(monthlyIncomeAtRetirement, yearsToRetirement);
  const pensionIncomeAtRetirement = getPensionIncomeAtAge(retirementAge);
  const totalMonthlyIncome = inflationAdjustedIncome + pensionIncomeAtRetirement;

  // Calculate for income mode
  // For income mode, we need to account for inflation when calculating required portfolio
  // First, estimate years to target with non-inflation adjusted income
  const estimatedYears = calculateYearsToTarget(calculateRequiredPortfolio(targetMonthlyIncome));
  // Adjust target income for inflation at the estimated retirement age
  const futureTargetIncome = targetMonthlyIncome * Math.pow(1 + inflationRate / 100, estimatedYears);
  // Calculate the actual required portfolio with inflation-adjusted income
  const requiredPortfolio = calculateRequiredPortfolio(futureTargetIncome);
  const actualYearsToTarget = calculateYearsToTarget(requiredPortfolio);
  const actualTargetAge = currentAge + actualYearsToTarget;

  // Add pension
  const addPension = () => {
    setPensions([...pensions, {
      id: Date.now().toString(),
      name: 'New Pension',
      age: 65,
      monthlyAmount: 1000,
    }]);
  };

  // Remove pension
  const removePension = (id: string) => {
    setPensions(pensions.filter(p => p.id !== id));
  };

  // Update pension
  const updatePension = (id: string, field: keyof PensionIncome, value: string | number) => {
    setPensions(pensions.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Flame className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">FIRE Calculator</h2>
            <p className="text-sm text-gray-500">Financial Independence, Retire Early</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Currency:</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {Object.keys(EXCHANGE_RATES).map((curr) => (
                <option key={curr} value={curr}>
                  {curr} ({EXCHANGE_RATES[curr].symbol})
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Info className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Info Panel */}
      {showInfo && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>4% Rule:</strong> Withdraw 4% of your portfolio annually for sustainable retirement income</li>
            <li>• <strong>Inflation Adjusted:</strong> All future values are adjusted for inflation</li>
            <li>• <strong>Growth Projection:</strong> Based on your expected annual return and monthly contributions</li>
            <li>• <strong>Pension Income:</strong> Add state or private pensions that kick in at specific ages</li>
          </ul>
        </div>
      )}

      {/* Mode Toggle */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setMode('age')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            mode === 'age'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Plan by Age
        </button>
        <button
          onClick={() => setMode('income')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            mode === 'income'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <DollarSign className="w-4 h-4 inline mr-2" />
          Plan by Income
        </button>
      </div>

      {/* Basic Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Current Age */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Age
          </label>
          <input
            type="number"
            value={currentAge}
            onChange={(e) => {
              const age = Number(e.target.value);
              if (age >= 18 && age <= 100) {
                setCurrentAge(age);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            min="18"
            max="100"
          />
        </div>

        {/* Current Portfolio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Portfolio Value
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-semibold">
            {formatCurrency(currentPortfolioValue)}
          </div>
        </div>

        {/* Monthly Contribution */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Monthly Contribution
          </label>
          <input
            type="number"
            value={monthlyContribution}
            onChange={(e) => setMonthlyContribution(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            min="0"
            step="100"
          />
        </div>

        {/* Annual Return */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expected Annual Return (%)
          </label>
          <input
            type="number"
            value={annualReturn}
            onChange={(e) => setAnnualReturn(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            min="0"
            max="20"
            step="0.5"
          />
        </div>

        {/* Inflation Rate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expected Inflation Rate (%)
          </label>
          <input
            type="number"
            value={inflationRate}
            onChange={(e) => setInflationRate(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            min="0"
            max="10"
            step="0.1"
          />
        </div>

        {/* Withdrawal Rate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Safe Withdrawal Rate (%)
          </label>
          <input
            type="number"
            value={withdrawalRate}
            onChange={(e) => setWithdrawalRate(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            min="2"
            max="6"
            step="0.1"
          />
        </div>
      </div>

      {/* Main Calculator - Age Mode */}
      {mode === 'age' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Target Retirement Age: <span className="text-2xl font-bold text-primary-600">{retirementAge}</span>
          </label>
          <input
            type="range"
            value={retirementAge}
            onChange={(e) => setRetirementAge(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
            min={currentAge + 1}
            max="100"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{currentAge + 1}</span>
            <span>100</span>
          </div>

          {/* Results */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
              <div className="text-sm text-green-700 mb-1">Portfolio at {retirementAge}</div>
              <div className="text-2xl font-bold text-green-900">{formatCurrency(futurePortfolioValue)}</div>
              <div className="text-xs text-green-600 mt-1">In {yearsToRetirement} years</div>
            </div>

            <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200">
              <div className="text-sm text-amber-700 mb-1">Monthly Income (Future $)</div>
              <div className="text-2xl font-bold text-amber-900">{formatCurrency(monthlyIncomeAtRetirement)}</div>
              <div className="text-xs text-amber-600 mt-1">Nominal value</div>
            </div>

            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-700 mb-1">Monthly Income (Today's $)</div>
              <div className="text-2xl font-bold text-blue-900">{formatCurrency(inflationAdjustedIncome)}</div>
              <div className="text-xs text-blue-600 mt-1">Inflation adjusted</div>
            </div>

            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
              <div className="text-sm text-purple-700 mb-1">Total Monthly Income</div>
              <div className="text-2xl font-bold text-purple-900">{formatCurrency(totalMonthlyIncome)}</div>
              <div className="text-xs text-purple-600 mt-1">Including pensions (today's $)</div>
            </div>
          </div>

          {/* Revenue Timeline Summary */}
          {pensions.length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Income Timeline (Today's Money)
              </h4>
              <div className="space-y-2">
                {(() => {
                  const sortedPensions = [...pensions].sort((a, b) => a.age - b.age);
                  const milestones = [retirementAge, ...sortedPensions.map(p => p.age)]
                    .filter((age, index, arr) => arr.indexOf(age) === index)
                    .sort((a, b) => a - b);
                  
                  return milestones.map((age, index) => {
                    const nextAge = milestones[index + 1];
                    const portfolioIncome = adjustForInflation(monthlyIncomeAtRetirement, yearsToRetirement);
                    const pensionIncome = getPensionIncomeAtAge(age);
                    const totalIncome = portfolioIncome + pensionIncome;
                    
                    return (
                      <div key={age} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">
                            {nextAge ? `Age ${age} - ${nextAge - 1}` : `Age ${age}+`}
                          </span>
                          {pensionIncome > 0 && (
                            <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full">
                              +{formatCurrency(pensionIncome)} pension
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-slate-900">{formatCurrency(totalIncome)}/mo</div>
                          <div className="text-xs text-slate-500">{formatCurrency(totalIncome * 12)}/yr</div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Calculator - Income Mode */}
      {mode === 'income' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Target Monthly Income: <span className="text-2xl font-bold text-primary-600">{formatCurrency(targetMonthlyIncome)}</span>
          </label>
          <input
            type="range"
            value={targetMonthlyIncome}
            onChange={(e) => setTargetMonthlyIncome(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
            min="1000"
            max="50000"
            step="500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatCurrency(1000)}</span>
            <span>{formatCurrency(50000)}</span>
          </div>

          {/* Results */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
              <div className="text-sm text-orange-700 mb-1">Required Portfolio</div>
              <div className="text-2xl font-bold text-orange-900">{formatCurrency(requiredPortfolio)}</div>
              <div className="text-xs text-orange-600 mt-1">To generate income</div>
            </div>

            <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
              <div className="text-sm text-indigo-700 mb-1">Years to Goal</div>
              <div className="text-2xl font-bold text-indigo-900">{actualYearsToTarget.toFixed(1)}</div>
              <div className="text-xs text-indigo-600 mt-1">
                {actualYearsToTarget < 1 ? 'Already achieved!' : `At age ${Math.round(actualTargetAge)}`}
              </div>
            </div>

            <div className="p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg border border-pink-200">
              <div className="text-sm text-pink-700 mb-1">Target Retirement Age</div>
              <div className="text-2xl font-bold text-pink-900">{Math.round(actualTargetAge)}</div>
              <div className="text-xs text-pink-600 mt-1">
                {actualYearsToTarget < 1 ? 'Retire now!' : `In ${Math.round(actualYearsToTarget)} years`}
              </div>
            </div>

            <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border border-teal-200">
              <div className="text-sm text-teal-700 mb-1">Future Income Value</div>
              <div className="text-2xl font-bold text-teal-900">{formatCurrency(futureTargetIncome)}</div>
              <div className="text-xs text-teal-600 mt-1">At retirement (future $)</div>
            </div>
          </div>

          {/* Revenue Timeline Summary */}
          {pensions.length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Income Timeline (Today's Money)
              </h4>
              <div className="space-y-2">
                {(() => {
                  const sortedPensions = [...pensions].sort((a, b) => a.age - b.age);
                  const retirementAgeForIncome = Math.round(actualTargetAge);
                  const milestones = [retirementAgeForIncome, ...sortedPensions.map(p => p.age)]
                    .filter((age, index, arr) => arr.indexOf(age) === index)
                    .sort((a, b) => a - b);
                  
                  return milestones.map((age, index) => {
                    const nextAge = milestones[index + 1];
                    const portfolioIncome = targetMonthlyIncome;
                    const pensionIncome = getPensionIncomeAtAge(age);
                    const totalIncome = portfolioIncome + pensionIncome;
                    
                    return (
                      <div key={age} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">
                            {nextAge ? `Age ${age} - ${nextAge - 1}` : `Age ${age}+`}
                          </span>
                          {pensionIncome > 0 && (
                            <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full">
                              +{formatCurrency(pensionIncome)} pension
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-slate-900">{formatCurrency(totalIncome)}/mo</div>
                          <div className="text-xs text-slate-500">{formatCurrency(totalIncome * 12)}/yr</div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pension Income Section */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-primary-600" />
            Pension Income
          </h3>
          <button
            onClick={addPension}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Pension
          </button>
        </div>

        {pensions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <PiggyBank className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No pensions added yet</p>
            <p className="text-sm">Add state or private pensions that will start at specific ages</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pensions.map((pension) => (
              <div key={pension.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="text"
                  value={pension.name}
                  onChange={(e) => updatePension(pension.id, 'name', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Pension name"
                />
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Age:</label>
                  <input
                    type="number"
                    value={pension.age}
                    onChange={(e) => updatePension(pension.id, 'age', Number(e.target.value))}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min={currentAge}
                    max="100"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Monthly:</label>
                  <input
                    type="number"
                    value={pension.monthlyAmount}
                    onChange={(e) => updatePension(pension.id, 'monthlyAmount', Number(e.target.value))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    min="0"
                    step="100"
                  />
                </div>
                <button
                  onClick={() => removePension(pension.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pension Timeline */}
        {pensions.length > 0 && (
          <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Income Timeline</h4>
            <div className="space-y-2">
              {[...pensions]
                .sort((a, b) => a.age - b.age)
                .map((pension, index) => {
                  const previousIncome = index > 0 
                    ? pensions.filter(p => p.age <= pension.age).reduce((sum, p) => sum + p.monthlyAmount, 0)
                    : pension.monthlyAmount;
                  
                  return (
                    <div key={pension.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        Age {pension.age}: <span className="font-medium text-gray-900">{pension.name}</span>
                      </span>
                      <span className="font-semibold text-primary-600">
                        {formatCurrency(previousIncome)}/month total
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{yearsToRetirement}</div>
            <div className="text-xs text-gray-500">Years to Retirement</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(monthlyContribution * 12 * yearsToRetirement)}
            </div>
            <div className="text-xs text-gray-500">Total Contributions</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(futurePortfolioValue - currentPortfolioValue - (monthlyContribution * 12 * yearsToRetirement))}
            </div>
            <div className="text-xs text-gray-500">Investment Growth</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {((futurePortfolioValue / (currentPortfolioValue + monthlyContribution * 12 * yearsToRetirement) - 1) * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500">Total Return</div>
          </div>
        </div>
      </div>
    </div>
  );
}
